import { ArrowLeft, Circle, CircleCheckBig, Mail } from "lucide-react";
import { useEffect, useRef } from "react";
import { useMainStore } from "@/stores";
import { useNavigate } from "react-router-dom";
import { fetchCurrentUser } from "@/api/auth";
import {
  getTargetRoute,
  handleReturnToSignUp,
} from "@/shared/utils/helperFunctions";
import { showSuccess } from "@/shared/utils/toast";

const POLL_INTERVAL_MS = 10_000;

const formatSubmitDate = (dateStr) => {
  const date = dateStr ? new Date(dateStr) : new Date();
  return (
    date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }) +
    " at " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  );
};

const AccessRequestStatus = () => {
  const { user, setUser } = useMainStore();
  const isOrgRequest = user?.requestTarget === "org";
  const orgName = user?.orgName || "your organization";
  const navigate = useNavigate();
  const pollingRef = useRef(null);

  useEffect(() => {
    let active = true;

    const poll = async () => {
      try {
        const fresh = await fetchCurrentUser();
        if (!active || !fresh?.id) return;

        if (fresh.approvalStatus === "approved") {
          const updated = { ...user, ...fresh, approvalStatus: "approved" };
          setUser(updated);
          showSuccess("Your request has been approved!");
          navigate(
            getTargetRoute(updated, {
              appMode: useMainStore.getState().appMode ?? "user",
            }),
            { replace: true },
          );
          return;
        }

        if (fresh.approvalStatus === "rejected") {
          setUser({ ...user, ...fresh, approvalStatus: "rejected" });
          navigate("/join-organization/rejected", { replace: true });
          return;
        }
      } catch {
        // network blip -- retry next interval
      }
    };

    pollingRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(pollingRef.current);
    };
  }, [user, setUser, navigate]);

  const timelineSteps = [
    {
      icon: (
        <div className="w-9 h-9 rounded-full bg-[#00A63E] flex items-center justify-center flex-shrink-0">
          <CircleCheckBig size={20} className="text-white" />
        </div>
      ),
      title: "Request Submitted",
      subtitle: formatSubmitDate(user?.requestSubmittedAt),
      lineColor: "bg-success-200",
    },
    {
      icon: (
        <div className="w-9 h-9 rounded-full bg-warning-100 flex items-center justify-center flex-shrink-0">
          <Circle size={14} className="text-warning-500 fill-[#FE9A00]" />
        </div>
      ),
      title: "Awaiting Admin Approval",
      subtitle: isOrgRequest
        ? "Notification sent to your organization admin"
        : "Request sent to SalesWorx admin",
      lineColor: "bg-gray-200",
    },
    {
      icon: (
        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
          <Circle size={14} className="text-gray-500 fill-gray-500" />
        </div>
      ),
      title: "Access Granted",
      subtitle: "You'll receive an email when approved",
      lineColor: null,
    },
  ];

  const statusTitle = isOrgRequest
    ? "Access Request Sent"
    : "Request Sent to SalesWorx";
  const statusMessage = isOrgRequest
    ? `Your request to join ${orgName} has been submitted.`
    : "Your request has been submitted to the SalesWorx team. You'll receive an email when your workspace is approved.";

  const whatHappensNext = isOrgRequest
    ? "Your organization admin will review your request and can approve or deny access from their team management dashboard. You'll receive an email notification with the decision."
    : "The SalesWorx team will review your request and set up your workspace. You'll receive an email notification when your workspace is ready.";

  return (
    <div className="bg-[#F9FAFB] flex items-center justify-center h-[100dvh] w-full">
      <div className="flex items-start  gap-4  w-[90%]">
        <div
          className="flex items-center gap-1.5 cursor-pointer mt-4"
          onClick={() => handleReturnToSignUp(navigate)}
        >
          {" "}
          <ArrowLeft
            strokeWidth={1.5}
            size={20}
            className="text-secondary cursor-pointer"
          />
          <button className="text-secondary font-normal text-[0.85rem] hover:underline transition-all duration-300 w-fit">
            Return to Login
          </button>
        </div>

        <div className="border border-gray-200 rounded-2xl p-6  bg-white flex flex-col mx-auto gap-[22px] shadow-sm max-w-[540px]">
          {/* Green checkmark */}
          <div className="flex flex-col items-center gap-3.5">
            <div className="w-16 h-16 rounded-full bg-success-100 flex items-center justify-center">
              <CircleCheckBig size={28} className="text-success-500" />
            </div>
            <h1 className="text-gray-800 text-[1.38rem] leading-8 font-medium">
              {statusTitle}
            </h1>
            <p className="text-gray-500 text-[0.9rem] leading-6 font-normal text-center">
              {statusMessage}
            </p>
          </div>

          {/* Request Timeline */}
          <div className="flex flex-col gap-0">
            <h3 className="text-gray-800 text-[0.85rem] mb-4">
              Request Timeline
            </h3>

            {timelineSteps.map((step, index) => (
              <div key={index} className="flex gap-3.5 items-start">
                <div className="flex flex-col items-center">
                  {step.icon}
                  {step.lineColor && (
                    <div className={`w-[2px] h-8 ${step.lineColor}`} />
                  )}
                </div>
                <div className="flex flex-col pt-1.5">
                  <h5 className="text-gray-800 text-[0.85rem] leading-5 font-medium">
                    {step.title}
                  </h5>
                  <p className="text-gray-500 text-[0.7rem] leading-[18px] font-normal">
                    {step.subtitle}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* What happens next info box */}
          <div className="flex gap-2 p-4 bg-[#207C971A] rounded-[10px] border border-secondary">
            <Mail size={18} className="text-secondary flex-shrink-0" />
            <div className="flex flex-col">
              <h5 className="text-secondary text-[0.85rem] font-medium">
                What happens next?
              </h5>
              <p className="text-secondary text-[0.85rem] leading-5 font-normal">
                {whatHappensNext}
              </p>
            </div>
          </div>
          {/* divider */}
          <div className="h-[0.5px] bg-gray-200" />
          {/* need help info */}
          <p className="text-gray-500 text-[0.7rem] text-center leading-[18px] font-normal">
            Need help?{" "}
            <span className="text-secondary cursor-pointer hover:underline transition-all duration-300">
              Contact Support
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccessRequestStatus;
